import { motion } from "framer-motion";
import { Heart, Zap, ShieldCheck, Plus } from "lucide-react";
import { IMAGES, reveal, NPN, money, PRICE_PER_BOTTLE } from "@/components/store/data";

const FEATURES = [
  { icon: Zap, title: "Sustained Energy", body: "Fuels mitochondrial ATP for all-day stamina." },
  { icon: Heart, title: "Heart Support", body: "Nourishes the hardest-working muscle in your body." },
  { icon: ShieldCheck, title: "Antioxidant", body: "Fights oxidative stress that drives cellular ageing." },
];

export default function Benefits({ onAdd }) {
  return (
    <section id="benefits" className="relative py-24 md:py-32 bg-[#08302D] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-14">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-5">Why It Works</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#F4F1EB] max-w-2xl leading-[0.95]">
            One softgel. Three pillars of longevity.
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-5">
          {/* Big image tile */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="lg:col-span-7 relative overflow-hidden rounded-sm border border-white/10 min-h-[340px]"
          >
            <img src={IMAGES.softgels} alt="CoQ10 softgels" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#052221] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-[#B8C5C4] text-xs uppercase tracking-[0.2em] mb-1">Pharmaceutical grade</p>
              <p className="font-display text-2xl text-[#F4F1EB]">200 mg active Ubiquinol · 60 softgels</p>
            </div>
          </motion.div>

          {/* Feature stack */}
          <div className="lg:col-span-5 grid gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                variants={reveal}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="flex items-start gap-5 p-6 bg-[#0C3F3B] border border-white/10 rounded-sm"
              >
                <f.icon className="w-6 h-6 text-[#D4AF37] shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl text-[#F4F1EB] mb-1">{f.title}</h3>
                  <p className="text-[#B8C5C4] text-sm leading-relaxed">{f.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* facts bar */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mt-8 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden"
        >
          {[["NPN", NPN], ["Form", "Reduced Ubiquinol"], ["Price", `${money(PRICE_PER_BOTTLE)} CAD / bottle`]].map(([k, v]) => (
            <div key={k} className="bg-[#052221] p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#B8C5C4] mb-2">{k}</p>
              <p className="font-display text-xl text-[#F4F1EB]">{v}</p>
            </div>
          ))}
        </motion.div>

        <div className="mt-10">
          <button
            data-testid="benefits-add-btn"
            onClick={onAdd}
            className="group flex items-center gap-3 bg-[#D4AF37] text-[#052221] px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-[#F0C851] transition-colors"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
            Add a bottle to basket
          </button>
        </div>
      </div>
    </section>
  );
}
